import { dragonBones } from 'dragonbones-pixi';
import 'lib/pixi-ui/pixi-ui';
import Viewport from 'pixi-viewport';
import * as PIXI from 'pixi.js';
import UIScrollComponent from '../../Component/UI/Scroll';
import ViewportComponent from '../../Component/World/Viewport';
import Game from '../../Game';
import GameObject from '../../GameObject/GameObject';
import IRenderer from './IRenderer';
import Container = PIXI.Container;
import Sprite = PIXI.Sprite;
import Texture = PIXI.Texture;
import Element = PIXI.ui.Element;

const GAME_WIDTH: number = 1280;
const GAME_HEIGHT: number = 800;
const RATIO: number = GAME_WIDTH / GAME_HEIGHT;
export const WORLD_WIDTH: number = 1920;
const WORLD_HEIGHT: number = 4320;

export default class PIXIRenderer implements IRenderer {
    public static renderer: PIXI.WebGLRenderer | PIXI.CanvasRenderer;
    public static sceneContainer: PIXI.Container;
    public static rootContainer: PIXI.Container;
    public static mapViewport: Viewport;
    public static dbFactory: dragonBones.PixiFactory;
    public static viewports: Viewport[];

    public static render() {
        try {
            PIXIRenderer.renderer.render(PIXIRenderer.rootContainer);
        } catch (e) {
            // Skip "TypeError: t._frame.clone is not a function" error from older devices.
            // Canvas and bomb card explosion.
        }
    }

    public static resizeViewport(width: number, height: number): void {
        // Extra-special viewport resizing for world map.
        if (PIXIRenderer.mapViewport) {
            if (width < WORLD_WIDTH) {
                PIXIRenderer.mapViewport.resize(
                    width,
                    height,
                    WORLD_WIDTH,
                    WORLD_HEIGHT
                );
            } else {
                PIXIRenderer.mapViewport.resize(
                    width,
                    height,
                    width,
                    WORLD_HEIGHT
                );
            }
        }
    }

    public static resizeUI(width: number, height: number): void {
        const elements = PIXIRenderer.getChildrenByType(
            PIXIRenderer.rootContainer,
            Element
        );
        for (let i = 0, n = elements.length; i < n; i++) {
            const element = elements[i];
            if (!(element.parent instanceof Element)) {
                element.resize(width, height);
            }
        }
    }

    public static getChildrenByType<T extends Container>(
        container: Container,
        ctor: { new (): T }
    ): T[] {
        return container.children.reduce(
            (acc: T[], val: Container) => {
                return acc.concat(PIXIRenderer.getChildrenByType<T>(val, ctor));
            },
            container instanceof ctor ? [container] : []
        );
    }

    public static getTexture(texture: string): Texture {
        if (texture) {
            return Game.resource.get<Texture>(texture);
        }

        // Default texture.
        return Texture.EMPTY;
    }

    public static displayObjectToBase64(sprite: PIXI.DisplayObject): string {
        return PIXIRenderer.renderer.plugins.extract.base64(sprite);
    }

    public static textureToBase64(textureString: string): string {
        const texture: Texture = PIXIRenderer.getTexture(textureString);

        if (texture) {
            const tempSprite = new Sprite(texture);
            const base64Sprite = PIXIRenderer.renderer.plugins.extract.base64(
                tempSprite
            );
            tempSprite.destroy();

            return base64Sprite;
        } else {
            return '';
        }
    }

    private static addViewport(viewport: Viewport) {
        if (PIXIRenderer.viewports.indexOf(viewport) === -1)
            PIXIRenderer.viewports.push(viewport);
    }

    private static removeViewport(viewport: Viewport) {
        const index = PIXIRenderer.viewports.indexOf(viewport);
        if (index !== -1) PIXIRenderer.viewports.splice(index, 1);
    }
    public sceneContainer: PIXI.Container;

    public init(sceneName: string, sceneIndex: number): void {
        this.sceneContainer = new PIXI.Container();
        this.sceneContainer.name = sceneName;

        PIXIRenderer.sceneContainer.addChildAt(
            this.sceneContainer,
            Math.max(
                0,
                Math.min(
                    PIXIRenderer.sceneContainer.children.length,
                    sceneIndex - 1
                )
            )
        );
    }

    public start(): void {
        PIXI.glCore.VertexArrayObject.FORCE_NATIVE = true;
        PIXIRenderer.renderer = PIXI.autoDetectRenderer(
            window.innerWidth,
            window.innerHeight,
            {
                view: document.getElementById('canvas') as HTMLCanvasElement,
                antialias: false,
                transparent: false,
                clearBeforeRender: false,
                roundPixels: false,
                legacy: true,
                autoResize: false,
            },
            Game.config.forceCanvas
        );
        PIXI.settings.MESH_CANVAS_PADDING = 2;

        // Disable default ticker.
        const ticker = PIXI.ticker.shared;
        ticker.autoStart = false;
        ticker.stop();

        PIXIRenderer.renderer.plugins.interaction.autoPreventDefault = true;

        PIXIRenderer.renderer.view.className = 'renderer';
        PIXIRenderer.renderer.backgroundColor = 0xffffff;
        PIXIRenderer.dbFactory = dragonBones.PixiFactory.factory;

        window.document.body.appendChild(PIXIRenderer.renderer.view);

        // Canvas drag fix for Edge.
        PIXIRenderer.renderer.view.ondragstart = () => false;

        // Default containers:
        PIXIRenderer.rootContainer = new PIXI.Container();
        PIXIRenderer.rootContainer.name = 'RootContainer';
        PIXIRenderer.sceneContainer = new PIXI.Container();
        PIXIRenderer.sceneContainer.name = 'SceneContainer';
        PIXIRenderer.rootContainer.addChild(PIXIRenderer.sceneContainer);

        // Static viewports.
        PIXIRenderer.viewports = [];
    }

    public destroy(): void {
        this.sceneContainer.destroy();
        this.sceneContainer = null;
    }

    public getRenderer() {
        return PIXIRenderer.renderer;
    }

    public bringToFront(gameObject: GameObject): void {
        if (gameObject.container && gameObject.container.parent) {
            gameObject.container.parent.addChild(gameObject.container);
        }
    }

    public sortChildren(gameObject: GameObject): void {
        if (gameObject.container) {
            gameObject.container.children
                .sort((a, b) => b.position.y - a.position.y)
                .forEach(child => {
                    gameObject.container.addChildAt(child, 0);
                });
        }
    }

    public addGameObject(gameObject: GameObject): void {
        if (!gameObject.parent) {
            this.sceneContainer.addChild(gameObject.container);
        }

        const elements: Element[] = PIXIRenderer.getChildrenByType(
            gameObject.container,
            Element
        );
        if (gameObject.container instanceof Element) {
            elements.push(gameObject.container);
        }

        elements.forEach(e => {
            e.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        });

        this.addViewports(gameObject);
    }

    public removeGameObject(gameObject: GameObject): void {
        if (!gameObject.parent)
            this.sceneContainer.removeChild(gameObject.container);

        this.removeViewports(gameObject);
    }

    public update(): void {
        const delta = Game.ticker.getDeltaTime();

        // Update viewports manually instead of by the default shared PIXI ticker.
        for (let i = 0, n = PIXIRenderer.viewports.length; i < n; i++) {
            // @ts-ignore
            PIXIRenderer.viewports[i].update(delta);
        }

        // @ts-ignore
        dragonBones.PixiFactory._dragonBonesInstance.advanceTime(delta * 0.001);

        PIXIRenderer.render();
    }

    public stop(): void {}

    public resize(width: number, height: number): void {
        let newWidth: number;
        let newHeight: number;
        let top: number = 0;

        if (height > width) {
            // portrait mode
            newWidth = GAME_WIDTH;
            newHeight = GAME_HEIGHT;

            const scaleFactor: number = Math.min(
                width / GAME_WIDTH,
                height / GAME_HEIGHT
            );

            top = Math.floor((height - GAME_HEIGHT * scaleFactor) / 2);
        } else {
            // landscape mode
            if (width / height >= RATIO) {
                newHeight = GAME_HEIGHT;
                newWidth = Math.floor(newHeight * (width / height));
            } else {
                newWidth = GAME_WIDTH;
                newHeight = Math.floor(newWidth * (height / width));
            }
        }

        PIXIRenderer.renderer.view.style.position = 'absolute';
        PIXIRenderer.renderer.view.style.top = `${top}px`;

        PIXIRenderer.renderer.resize(newWidth, newHeight);
        PIXIRenderer.resizeViewport(newWidth, newHeight);
        PIXIRenderer.resizeUI(newWidth, newHeight);
        PIXIRenderer.resizeUI(newWidth, newHeight); // TODO: ???
    }

    private addViewports(gameObject: GameObject) {
        const viewportComponents = gameObject.getComponentsInChildren(
            ViewportComponent
        );
        for (let i = 0, n = viewportComponents.length; i < n; i++) {
            PIXIRenderer.addViewport(viewportComponents[i].container);
        }
        const scrollComponents = gameObject.getComponentsInChildren(
            UIScrollComponent
        );
        for (let i = 0, n = scrollComponents.length; i < n; i++) {
            PIXIRenderer.addViewport(scrollComponents[i].UIComponent.viewport);
        }
    }

    private removeViewports(gameObject: GameObject) {
        const viewportComponents = gameObject.getComponentsInChildren(
            ViewportComponent
        );
        for (let i = 0, n = viewportComponents.length; i < n; i++) {
            PIXIRenderer.removeViewport(viewportComponents[i].container);
        }
        const scrollComponents = gameObject.getComponentsInChildren(
            UIScrollComponent
        );
        for (let i = 0, n = scrollComponents.length; i < n; i++) {
            PIXIRenderer.removeViewport(
                scrollComponents[i].UIComponent.viewport
            );
        }
    }
}
